'use client';

import { useCallback } from 'react';

import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';

import { useChatAPI } from '@/apis/chat_api';
import { conversationKeys, useRenameConversation } from '@/apis/conversations';
import { PaginatedConversationsResponse } from '@/app/(authed-react-query)/(authed-sidebars)/chats/types';
import { useChatContext } from '@/contexts/chat_context';

type ChatHistoryData = InfiniteData<PaginatedConversationsResponse>;

interface UseConversationActionsOptions {
  isFavorite: (id: string) => boolean;
  toggleFavorite: (id: string) => void;
}

export function useConversationActions({
  isFavorite,
  toggleFavorite
}: UseConversationActionsOptions) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { activeConversationId, resetChat } = useChatContext();
  const { deleteConversation } = useChatAPI();

  const renameMutation = useRenameConversation();

  const deleteMutation = useMutation({
    mutationFn: async (conversationId: string) => {
      const response = (await deleteConversation(conversationId)) as Response;
      if (!response.ok) {
        throw new Error('Failed to delete conversation');
      }
    },
    onMutate: async (conversationId) => {
      await queryClient.cancelQueries({ queryKey: ['chat-history'] });
      const previous = queryClient.getQueriesData<ChatHistoryData>({
        queryKey: ['chat-history']
      });

      queryClient.setQueriesData<ChatHistoryData>({ queryKey: ['chat-history'] }, (data) => {
        if (!data) return data;
        return {
          ...data,
          pages: data.pages.map((page) => {
            if (!page.conversations?.[conversationId]) return page;
            const { [conversationId]: _removed, ...rest } = page.conversations;
            return { ...page, conversations: rest };
          })
        };
      });

      const wasFavorite = isFavorite(conversationId);
      if (wasFavorite) {
        toggleFavorite(conversationId);
      }

      if (activeConversationId === conversationId) {
        router.push('/chat');
        resetChat();
      }

      return { previous, wasFavorite };
    },
    onError: (_error, conversationId, context) => {
      context?.previous?.forEach(([key, data]) => queryClient.setQueryData(key, data));
      if (context?.wasFavorite) {
        toggleFavorite(conversationId);
      }
      toast.error('Failed to delete conversation');
    },
    onSettled: (_data, _error, conversationId) => {
      queryClient.removeQueries({ queryKey: conversationKeys.detail(conversationId) });
      return queryClient.invalidateQueries({ queryKey: ['chat-history'] });
    }
  });

  const renameConversation = useCallback(
    async (conversationId: string, nextTitle: string | null) => {
      const normalizedTitle = nextTitle?.trim() ? nextTitle.trim() : null;
      await renameMutation.mutateAsync({ conversationId, name: normalizedTitle });
    },
    [renameMutation]
  );

  const handleDeleteConversation = useCallback(
    async (conversationId: string) => {
      await deleteMutation.mutateAsync(conversationId);
    },
    [deleteMutation]
  );

  return {
    renameConversation,
    deleteConversation: handleDeleteConversation
  };
}
